import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { eventApi } from '../../services/footballApi';

interface Event {
  id: number;
  match?: { name: string } | null;
  event_type?: string;
  minute?: number;
  player?: { name: string } | null;
  team?: { name: string } | null;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

const FootballEvents: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await eventApi.getAll();
      setEvents(res.data);
    } catch (err: any) {
      setError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEdit = (id: number) => {
    navigate(`/football/events/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await eventApi.delete(id);
      fetchEvents();
    } catch (err) {
      alert('Failed to delete event');
    }
  };

  const handleCreate = () => {
    navigate('/football/events/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'match', headerName: 'Match', flex: 1, minWidth: 160, valueGetter: (value: Event['match']) => value?.name || '' },
    { field: 'event_type', headerName: 'Type', width: 120 },
    { field: 'minute', headerName: 'Minute', width: 100, type: 'number' },
    { field: 'player', headerName: 'Player', flex: 1, minWidth: 120, valueGetter: (value: Event['player']) => value?.name || '' },
    { field: 'team', headerName: 'Team', flex: 1, minWidth: 120, valueGetter: (value: Event['team']) => value?.name || '' },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: Event }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Events</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Event
        </Button>
      </Stack>
      <DataGrid
        rows={events}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballEvents; 